import _ from 'underscore';
import { ChildRef } from '../../../../../lib/web/components/child-ref';
import { ClickListener } from '../../../../../lib/web/components/click-listener';
import { Component } from '../../../../../lib/web/components/component';
import { ComponentBase } from '../../../../../lib/web/components/component-base';
import { Input } from '../../../../../lib/web/components/input';
import { Key } from '../../../../../lib/web/core/key';
import { KeyDownListener } from '../../../../../lib/web/components/key-down-listener';
import { SubtitleSettingsComponent } from '../../molecules/subtitle-settings/subtitle-settings.component';
import { Agent } from '../../../../../lib/web/core/agent';

export interface AudioMenuData {
    player: { pause: () => void, play: () => void, isPlaying: () => boolean },
    fontSize: { getValue: () => number, setValue: (value: number) => void }
}

@Component({
    selector: '.a-audio-menu'
})

export class AudioMenuComponent extends ComponentBase<HTMLElement> {
    
    private _audioTracks: { enabled: boolean, label: string, language: string, id: string }[] = null;
    private _selectedAudioTrack: string = null;
    private _selectedAudioTrackLanguage: string = null;
    private _textTracks: { id: string, mode: 'disabled' | 'hidden' | 'showing', kind: string, label: string, language: string }[] = null;
    private _notDefaultTextTracks: { id: string, mode: 'disabled' | 'hidden' | 'showing', kind: string, label: string, language: string }[] = null;
    private _selectedTextTrack: string = null;
    private _selectedTextTrackLanguage: string = null;

    @Input('locales', { isJSON: true })
    private _locales: { code: string, name: string }[] = null;

    @ChildRef('.a-audio-menu__main-menu')
    private _mainMenu: HTMLDivElement = null;
    @ChildRef('.a-audio-menu__subtitle-menu')
    private _subtitlesMenu: HTMLDivElement = null;
    @ChildRef('.a-audio-menu__audio-menu')
    private _audioMenu: HTMLDivElement = null;

    @ChildRef('.a-audio-menu__main-menu-subtitle')
    private _subtitlesMainMenu: HTMLUListElement = null;
    @ChildRef('.a-audio-menu__main-menu-audio')
    private _audioMainMenu: HTMLUListElement = null;

    @ChildRef('.a-audio-menu__subtitle-menu-items')
    private _subtitleMenuListElement: HTMLUListElement = null;
    @ChildRef('.a-audio-menu__audio-menu-items')
    private _audioMenuListElement: HTMLUListElement = null;

    @ChildRef('.a-audio-menu__subtitle-menu-title-container')
    private _subtitleMenuBack: HTMLDivElement = null;
    @ChildRef('.a-audio-menu__audio-menu-title-container')
    private _audioMenuBack: HTMLDivElement = null;

    @ChildRef('.a-audio-menu__main-menu-subtitle-value')
    private _subtitleValue: HTMLDivElement = null;
    @ChildRef('.a-audio-menu__main-menu-audio-value')
    private _audioValue: HTMLDivElement = null;

    @ChildRef()
    private _subtitleSettings: SubtitleSettingsComponent = null;
    
    private _data: AudioMenuData = null;
    private _isSubtitlesSettingsShown: boolean = false;

    public constructor(node: HTMLElement) {
        super(node);
    }

    @ClickListener('.a-audio-menu__main-menu-subtitle')
    public onSubtitlesClicked(e: MouseEvent): void {
        this.showSubtitlesMenu();
    }

    @KeyDownListener(Key.Enter, '.a-audio-menu__main-menu-subtitle')
    public onSubtitlesEnter(): void {
        this.showSubtitlesMenu();
    }

    @ClickListener('.a-audio-menu__main-menu-audio')
    public onAudioClicked(e: MouseEvent): void {
        this.showAudioMenu();
    }

    @KeyDownListener(Key.Enter, '.a-audio-menu__main-menu-audio')
    public onAudioEnter(): void {
        this.showAudioMenu();
    }
    

    public get selectedAudioTrack(): string {
        if (this._selectedAudioTrack && Number.isNaN(Number(this._selectedAudioTrack))) {
            return this._selectedAudioTrack;
        }
        else {
            return this._audioTracks?.find(track => this.getTrackId(track) === this._selectedAudioTrack)?.label || '';
        }
    }

    public set selectedAudioTrack(value: string) {
        this._selectedAudioTrack = value;
    }
    
    public get selectedAudioTrackIndex(): number {
        return this._audioTracks?.findIndex(track => this.getTrackId(track) === this._selectedAudioTrack);
    }

    public get selectedTextTrack(): string {
        return this._selectedTextTrack;
    }

    public set selectedTextTrack(value: string) {
        this._selectedTextTrack = value;
    }

    public get selectedTextTrackIndex(): number {
        return this._textTracks.findIndex(track => track.language === this._selectedTextTrack);
    }

    public selectAudioTrack(id: string): void {
        if (this._audioMenuListElement) {
            const itemElement: HTMLLIElement = this._audioMenuListElement.querySelector(`li[data-language="${id.toLowerCase()}"]`) || this._audioMenuListElement.querySelector(`li[data-id="${id.toLowerCase()}"]`);
            if (itemElement) {
                itemElement.click();
            }
        }
    }

    public get selectedAudioTrackLanguage(): string {
        return this._selectedAudioTrackLanguage;
    }

    public selectTextTrackLanguage(language: string): void {
        if (this._subtitleMenuListElement) {
            const itemElement: HTMLLIElement = this._subtitleMenuListElement.querySelector(`li[data-language="${language.toLowerCase()}"]`);        
            if (itemElement) {
                itemElement.click();
            }
        }
    }

    public get selectedTextTrackLanguage(): string {
        return this._selectedTextTrackLanguage;
    }

    private showMainMenu(): void {
        this._mainMenu.classList.add('a-audio-menu__main-menu--show');
        this._subtitlesMenu.classList.remove('a-audio-menu__subtitle-menu--show');
        this._subtitleMenuListElement.querySelectorAll('li').forEach(subtitleElement => {
            subtitleElement.tabIndex = -1;
        });
        this._audioMenu.classList.remove('a-audio-menu__audio-menu--show');
        this._audioMenuListElement.querySelectorAll('li').forEach(audioElement => {
            audioElement.tabIndex = -1;
        });
    }

    private showSubtitlesMenu(): void {
        this._mainMenu.classList.remove('a-audio-menu__main-menu--show');
        this._audioMenu.classList.remove('a-audio-menu__audio-menu--show');
        this._subtitlesMenu.classList.add('a-audio-menu__subtitle-menu--show');
        this._subtitleMenuListElement.querySelectorAll('li').forEach(audioElement => {
            audioElement.tabIndex = 0;
        });
        this.focusSectionMenu(this._subtitleMenuListElement, this._subtitleMenuBack, 'a-audio-menu__subtitle-menu-item--selected');
    }

    private showAudioMenu(): void {
        this._mainMenu.classList.remove('a-audio-menu__main-menu--show');
        this._subtitlesMenu.classList.remove('a-audio-menu__subtitle-menu--show');
        this._audioMenu.classList.add('a-audio-menu__audio-menu--show');
        this._audioMenuListElement.querySelectorAll('li').forEach(audioElement => {
            audioElement.tabIndex = 0;
        });
        this.focusSectionMenu(this._audioMenuListElement, this._audioMenuBack, 'a-audio-menu__audio-menu-item--selected');
    }

    // With a single section there is no main menu to go back to, so the back
    // control is skipped and focus goes to the selected item instead
    private focusSectionMenu(listElement: HTMLUListElement, backElement: HTMLElement, selectedClassName: string): void {
        if (this.singleSection) {
            backElement.tabIndex = -1;
            const selectedElement: HTMLLIElement = listElement.querySelector(`.${selectedClassName}`) || listElement.querySelector('li:not([tabindex="-1"])');
            selectedElement?.focus();
        }
        else {
            backElement.focus();
        }
    }

    private onBack(): void {
        if (!this.singleSection) {
            this.showMainMenu();
        }
    }

    private get singleSection(): 'audio' | 'subtitles' | null {
        if (this.hasAudioOptions && !this.hasSubtitleOptions) {
            return 'audio';
        }
        if (this.hasSubtitleOptions && !this.hasAudioOptions) {
            return 'subtitles';
        }
        return null;
    }

    private show(): void {
        this.removeClass('a-audio-menu--hide');
        this.addClass('a-audio-menu--show');
        const singleSection: 'audio' | 'subtitles' | null = this.singleSection;
        this._node.classList.toggle('a-audio-menu--single-section', singleSection != null);
        if (singleSection == 'audio') {
            this.showAudioMenu();
        }
        else if (singleSection == 'subtitles') {
            this.showSubtitlesMenu();
        }
        else {
            this.showMainMenu();
        }
        this._subtitlesMainMenu.tabIndex = this.hasSubtitleOptions ? 0 : -1;
        this._audioMainMenu.tabIndex = this.hasAudioOptions ? 0 : -1;
        this.adjustHorizontalPosition();
    }

    public hide(): void {
        this._subtitleSettings?.close();
        this.removeClass('a-audio-menu--show');
        this._subtitlesMainMenu.tabIndex = -1;
        this._audioMainMenu.tabIndex = -1;
        this._node.style.transform = '';
    }

    private adjustHorizontalPosition(): void {
        requestAnimationFrame(() => {
            if (!this._node.classList.contains('a-audio-menu--show')) {
                return;
            }
            this._node.style.transform = '';
            const rect: DOMRect = this._node.getBoundingClientRect();
            const viewportWidth: number = window.innerWidth || document.documentElement.clientWidth;
            const padding: number = 8;

            let deltaX: number = 0;
            if (rect.left < padding) {
                deltaX = padding - rect.left;
            }
            else if (rect.right > viewportWidth - padding) {
                deltaX = (viewportWidth - padding) - rect.right;
            }

            if (deltaX !== 0) {
                this._node.style.transform = `translateX(${deltaX}px)`;
            }
        });
    }

    public toggle(): void {
        if (this._node.classList.contains('a-audio-menu--show')) {
            this.hide();       
        } 
        else {
            this.show();
        }
    }

    public addAudioTracks(value: { enabled: boolean, label: string, language: string , id: string }[]):void {
        this._audioTracks =[...value];
        const enabledTrack: { language: string , id: string } = value.find(audioTrack => audioTrack.enabled);
        this._selectedAudioTrack = this.getTrackId(enabledTrack) || null;
        this._selectedAudioTrackLanguage = enabledTrack?.language || null;
        this._audioValue.innerHTML = this.getLocale(enabledTrack?.language) || USER_LOCALE.disabled;
        this.setAudioMenu();
        this._audioMainMenu.classList.toggle('a-audio-menu__main-menu-audio--show', this.hasAudioOptions);
    }

    public addTextTracks(value: { id: string, mode: 'disabled' | 'hidden' | 'showing', kind: string, label: string, language: string, default: boolean }[]): void {
        this._textTracks = value.filter(textTrack => textTrack.kind === 'subtitles' && (Agent.safari() || textTrack.default !== undefined));
        this._notDefaultTextTracks = Agent.safari() ? []: value.filter(textTrack => textTrack.kind === 'subtitles' && textTrack.default === undefined);
        const enabledTrack: { id: string, label: string, language: string } = value.find(textTrack => textTrack.mode === 'showing');  
        this._selectedTextTrack = enabledTrack?.language || null;
        this._selectedTextTrackLanguage = enabledTrack?.language || null;
        this._subtitleValue.innerHTML = this.getLocale(enabledTrack?.language) || USER_LOCALE.disabled;
        this.setSubtitlesMenu();
        this._subtitlesMainMenu.classList.toggle('a-audio-menu__main-menu-subtitle--show', this.hasSubtitleOptions);
    }

    private setAudioMenu(): void {
        // Add back navigation
        this._audioMenuBack.addEventListener('click', () => {
            this.onBack();
        });

        this._audioMenuBack.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                this.onBack();
            }
        });

        this._audioTracks.forEach((audioTrack, index, array) => {
            const audioElement: HTMLLIElement = document.createElement('li');
            audioElement.classList.add('a-audio-menu__audio-menu-item');
            audioElement.innerHTML = this.getLocale(audioTrack.language);
            if (audioTrack.label.toString().toLowerCase().includes('(description)') || audioTrack.label.toString().toLowerCase().includes('(descriptive)')) {
                audioElement.innerHTML += ` (AD)`;
            }
            audioElement.role = 'button';
            audioElement.dataset.id = this.getTrackId(audioTrack).toLowerCase();
            audioElement.dataset.language = audioTrack.language.toLowerCase();
            if (audioTrack.enabled) {
                this._selectedAudioTrack = this.getTrackId(audioTrack);
                this._selectedAudioTrackLanguage = audioTrack.language;
                audioElement.classList.add('a-audio-menu__audio-menu-item--selected');                
            }            

            audioElement.addEventListener('click', () => this.audiosClick(audioTrack));
            audioElement.addEventListener('keydown', (e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                   this.audiosClick(audioTrack);
                }
            });
            this._audioMenuListElement.ariaSetSize = this._audioTracks.length.toString();
            this._audioMenuListElement.appendChild(audioElement);
        });
    }
    
    private audiosClick(audioTrack?: any) {
        this._selectedAudioTrack = audioTrack ? this.getTrackId(audioTrack) : null;
        this._selectedAudioTrackLanguage = audioTrack ? audioTrack.language : null;
        this._audioValue.innerHTML = this.getLocale(audioTrack.language);
        if (audioTrack.label.toString().toLowerCase().includes('(description)') || audioTrack.label.toString().toLowerCase().includes('(descriptive)')) {
            this._audioValue.innerHTML += ` (AD)`;
        }
        this.dispatchCustomEvent('audio-track-changed');
        this.hide();
        this.updateAudioMenu();        
    }

    private subtitlesClick(textTrack?: any) {
        this._selectedTextTrack = textTrack ? textTrack.language : 'disabled';
        this._selectedTextTrackLanguage = textTrack ? textTrack.language : null;
        this._subtitleValue.innerHTML = textTrack ? this.getLocale(textTrack.language) : USER_LOCALE.disabled;
        this.dispatchCustomEvent('text-track-changed');
        this.hide();
        this.updateSubtitlesMenu();
    }

    private setSubtitlesMenu(): void {
        // Add back navigation
        this._subtitleMenuBack.addEventListener('click', () => {
            this.onBack();
        });

        this._subtitleMenuBack.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                this.onBack();
            }
        });

        if (!Agent.safari()) {
            const settingsTextElement: HTMLLIElement = document.createElement('li');
            settingsTextElement.classList.add('a-audio-menu__subtitle-menu-item', 'a-audio-menu__subtitle-settings');
            settingsTextElement.innerHTML = USER_LOCALE.subtitlesSize;
            settingsTextElement.dataset.id = 'settings';
            settingsTextElement.addEventListener('click', async () => {
                this.addClass('a-audio-menu--hide');
                this._subtitlesMenu.classList.remove('a-audio-menu__subtitle-menu--show');
                const isPlaying: boolean = this._data.player.isPlaying();
                if (isPlaying) {
                    this._data.player.pause();
                }
                this._isSubtitlesSettingsShown = true;
                await this._subtitleSettings.open(this._data);
                this._isSubtitlesSettingsShown = false;
                this.hide();
                if (isPlaying) {
                    this._data.player.play();
                }
            });
            // TODO: Falta implementar evento de teclado
            this._subtitleMenuListElement.appendChild(settingsTextElement);                
        }

        this._textTracks.forEach((textTrack, index, array) => {
            const textElement: HTMLLIElement = document.createElement('li');
            textElement.classList.add('a-audio-menu__subtitle-menu-item');
            textElement.innerHTML = this.getLocale(textTrack.language);
            textElement.role = 'button';
            textElement.dataset.id = textTrack.language.toLowerCase();
            textElement.dataset.language = textTrack.language.toLowerCase();
            if (textTrack.mode === 'showing') {
                this._selectedTextTrack = textTrack.language;
                this._selectedTextTrackLanguage = textTrack.language;
                textElement.classList.add('a-audio-menu__subtitle-menu-item--selected');
            }
            textElement.addEventListener('click', () => this.subtitlesClick(textTrack));
            textElement.addEventListener('keydown', (e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                    this.subtitlesClick(textTrack)
                }
            });
            this._subtitleMenuListElement.appendChild(textElement);
        });

        const disabledTextElement: HTMLLIElement = document.createElement('li');
        disabledTextElement.classList.add('a-audio-menu__subtitle-menu-item');
        disabledTextElement.innerHTML = USER_LOCALE.disabled;
        disabledTextElement.role = 'button';
        disabledTextElement.dataset.id = 'disabled';
        if (!this._selectedTextTrack) {
            disabledTextElement.classList.add('a-audio-menu__subtitle-menu-item--selected');
        }
        disabledTextElement.addEventListener('click', () => this.subtitlesClick());
        disabledTextElement.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                this.subtitlesClick();
            }
        });
        this._subtitleMenuListElement.ariaSetSize = (this._textTracks.length + 1).toString();
        this._subtitleMenuListElement.appendChild(disabledTextElement);
    }

    // Chromecast exposes numeric track ids, while the HTML5 tech uses strings
    private getTrackId(track: { id: string | number }): string {
        return track?.id != null ? String(track.id) : null;
    }

    private updateAudioMenu(): void {
        this._audioMenuListElement.querySelectorAll('li').forEach(audioElement => {
            audioElement.classList.remove('a-audio-menu__audio-menu-item--selected');
            if (audioElement.dataset.language === this._selectedAudioTrack?.toLowerCase() || audioElement.dataset.id === this._selectedAudioTrack?.toLowerCase()) {
                audioElement.classList.add('a-audio-menu__audio-menu-item--selected');
            }
        });

        this._audioTracks.forEach(audioTrack => {
            audioTrack.enabled = false;
            if (this.getTrackId(audioTrack) === this._selectedAudioTrack) {
                audioTrack.enabled = true;
            }
        });
    }

    private updateSubtitlesMenu(): void {
        this._subtitleMenuListElement.querySelectorAll('li').forEach(textElement => {
            textElement.classList.remove('a-audio-menu__subtitle-menu-item--selected');
            if (textElement.dataset.language === this._selectedTextTrack?.toLowerCase() || textElement.dataset.id === this._selectedTextTrack?.toLowerCase()) {
                textElement.classList.add('a-audio-menu__subtitle-menu-item--selected');
            }
        });

        this._notDefaultTextTracks?.forEach(t => t.mode = 'disabled');
        this._textTracks.forEach(textTrack => {
            textTrack.mode = 'disabled';
            if (textTrack.language === this._selectedTextTrack) {
                textTrack.mode = 'showing';
            }
        });
    }

    private getLocale(value: string): string {
        const name: string = ((this._locales || []).find(l => l.code == value) || {}).name;
        return name || value;
    }

    public setData(value: AudioMenuData): void {
        this._data = value;
    }

    public get isSubtitlesSettingsShown(): boolean {
        return this._isSubtitlesSettingsShown;
    }

    public get hasAudioTracks(): boolean {
        return this._audioTracks?.length > 0;
    }

    public get hasAudioOptions(): boolean {
        return this._audioTracks?.length > 1;
    }

    public get hasSubtitleOptions(): boolean {
        return this._textTracks?.length > 0;
    }

    public get hasOptions(): boolean {
        return this.hasAudioOptions || this.hasSubtitleOptions;
    }

    // Label for the button that opens the menu, matching the available sections
    public get label(): string {
        switch (this.singleSection) {
            case 'audio':
                return USER_LOCALE.audio;
            case 'subtitles':
                return USER_LOCALE.subtitles;
            default:
                return USER_LOCALE.audioAndSubtitles;
        }
    }

    public unload(): void {
        while (this._subtitleMenuListElement.childNodes.length > 1) {
            this._subtitleMenuListElement.removeChild(this._subtitleMenuListElement.lastChild);
        }
        while (this._audioMenuListElement.childNodes.length > 1) {
            this._audioMenuListElement.removeChild(this._audioMenuListElement.lastChild);
        }
        this._audioTracks = null;
        this._selectedAudioTrack = null;
        this._selectedAudioTrackLanguage = null;
        this._textTracks = null;
        this._selectedTextTrack = null;
        this._selectedTextTrackLanguage = null;
        this._audioMainMenu.classList.remove('a-audio-menu__main-menu-audio--show');
        this._subtitlesMainMenu.classList.remove('a-audio-menu__main-menu-subtitle--show');
    }

    public dispose(): void {
        this.hide();
        super.dispose();
    }

    public setDisposable(value: boolean): void {
        this.isDisposable = value;
        this._subtitleSettings.setDisposable(value);
    }
}